import type {
  DimensionCatalogEntry,
  DimensionCatalogRollup,
  MetricRegistryAggregationRule,
  MetricRegistryAppliesTo,
  MetricRegistryBlendPolicy,
  MetricRegistryComponents,
  MetricRegistryEntry,
  MetricRegistryTargetRef,
  MetricTargetComparisonAnchor,
  MetricTemporalType,
} from './metric-registry';
import {
  GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
  METRIC_REGISTRY_GLOBAL_TENANT_ID,
} from './metric-registry';

// THE stored-row → registry-entry projection (moved verbatim from
// apps/api/src/semantic-layer/metric-query-compiler.ts, which re-imports it).
// Production recomputes each ACTIVE row's digest FROM this projection and
// refuses the row when it disagrees with the stored digest or compiled code —
// so the activation-reconciliation receipt must project with the SAME code, or
// a payload mutation behind an intact digest column could certify ACTIVATED
// for a registry the compiler rejects.

export type GovernedMetricRegistryEntryRowLike = Readonly<{
  tenantId?: string;
  registryLayer: string;
  industryProfileId?: string;
  companyMappingId?: string;
  metricKey: string;
  registryVersion: string;
  aliasesJson: unknown;
  label: string;
  definition: string;
  unit: string;
  frame: string;
  direction: string;
  temporalType: string;
  appliesToJson: unknown;
  blendPolicyJson: unknown;
  reconciliationRule: string;
  requiredEvidenceClassesJson: unknown;
  benchmarkable: boolean;
  grainSupportedJson: unknown;
  dimensionsSupportedJson: unknown;
  signConventionJson: unknown;
  sourceOfTruthClass: string;
  factScope?: unknown;
  composedFromJson?: unknown;
  targetMetricRefJson?: unknown;
  ownerModuleId?: string | null;
  neverSumWithJson: unknown;
  sourceBindingKeysJson: unknown;
  source: string;
}>;

export type GovernedMetricComponentRowLike = Readonly<{
  tenantId?: string;
  registryLayer?: string;
  industryProfileId?: string;
  companyMappingId?: string;
  metricKey: string;
  registryVersion: string;
  componentRole: string;
  componentMetricKey: string;
  temporalType: string;
}>;

export type GovernedMetricRollupGrainRowLike = Readonly<{
  tenantId?: string;
  registryLayer?: string;
  industryProfileId?: string;
  companyMappingId?: string;
  metricKey: string;
  registryVersion: string;
  dimension: string;
  rollup: string;
  formulaId: string;
  materialization: string;
}>;

export type GovernedDimensionCatalogRowLike = Readonly<{
  tenantId?: string;
  registryLayer?: string;
  industryProfileId?: string;
  companyMappingId?: string;
  registryVersion: string;
  dimension: string;
  label: string;
  physicalColumn: string;
  hierarchyJson: unknown;
  neverBlendAppliesToJson?: unknown;
  source: string;
}>;

export function projectGovernedMetricRegistryRow(
  row: GovernedMetricRegistryEntryRowLike,
  componentRows: readonly GovernedMetricComponentRowLike[],
  rollupRows: readonly GovernedMetricRollupGrainRowLike[],
): MetricRegistryEntry {
  const references = parseGovernedMetricDefinitionReferences(
    row.targetMetricRefJson,
  );
  return {
    tenantId: row.tenantId,
    metricKey: row.metricKey,
    registryLayer: row.registryLayer as MetricRegistryEntry['registryLayer'],
    industryProfileId: normalizeGlobalScope(row.industryProfileId),
    companyMappingId: normalizeGlobalScope(row.companyMappingId),
    registryVersion: row.registryVersion,
    aliases: parseStringArray(row.aliasesJson),
    appliesTo: parseAppliesTo(row.appliesToJson),
    label: row.label,
    definition: row.definition,
    unit: row.unit,
    frame: row.frame,
    direction: row.direction as MetricRegistryEntry['direction'],
    blendPolicy: parseBlendPolicy(row.blendPolicyJson),
    aggregationRules: rollupRows
      .filter((rollup) => sameRegistryChildScope(rollup, row))
      .map(mapRollupRule),
    reconciliationRule: row.reconciliationRule,
    requiredEvidenceClasses: parseStringArray(row.requiredEvidenceClassesJson),
    benchmarkable: row.benchmarkable,
    components: mapComponents(row, componentRows),
    temporalType: row.temporalType as MetricTemporalType,
    grainSupported: parseStringArray(row.grainSupportedJson),
    dimensionsSupported: parseStringArray(row.dimensionsSupportedJson),
    signConvention: parseSignConvention(row.signConventionJson, row.direction),
    sourceOfTruthClass: row.sourceOfTruthClass as MetricRegistryEntry['sourceOfTruthClass'],
    ...(row.factScope === 'gl_dataset_run' ? { factScope: row.factScope } : {}),
    composedFrom: parseComposition(row.composedFromJson),
    targetMetricRef: references.targetMetricRef,
    targetComparisonAnchor: references.targetComparisonAnchor,
    ownerModuleId: row.ownerModuleId ?? undefined,
    neverSumWith: parseStringArray(row.neverSumWithJson),
    sourceBindingKeys: parseStringArray(row.sourceBindingKeysJson),
    source: row.source,
  };
}

export function serializeGovernedMetricDefinitionReferences(
  entry: Pick<MetricRegistryEntry, 'targetMetricRef' | 'targetComparisonAnchor'>,
):
  | MetricRegistryTargetRef
  | Readonly<{
      targetMetricRef: MetricRegistryTargetRef | null;
      targetComparisonAnchor: MetricTargetComparisonAnchor;
    }>
  | undefined {
  if (!entry.targetComparisonAnchor) return entry.targetMetricRef;
  return {
    targetMetricRef: entry.targetMetricRef ?? null,
    targetComparisonAnchor: entry.targetComparisonAnchor,
  };
}

export function governedMetricDefinitionReferencesMatch(
  stored: unknown,
  compiled: Pick<MetricRegistryEntry, 'targetMetricRef' | 'targetComparisonAnchor'>,
): boolean {
  const projected = parseGovernedMetricDefinitionReferences(stored);
  return (
    sameTargetMetricRef(projected.targetMetricRef, compiled.targetMetricRef) &&
    sameTargetComparisonAnchor(
      projected.targetComparisonAnchor,
      compiled.targetComparisonAnchor,
    )
  );
}

export function projectGovernedDimensionCatalogRow(
  row: GovernedDimensionCatalogRowLike,
): DimensionCatalogEntry {
  return {
    tenantId: row.tenantId,
    registryLayer: row.registryLayer as DimensionCatalogEntry['registryLayer'],
    industryProfileId: normalizeGlobalScope(row.industryProfileId),
    companyMappingId: normalizeGlobalScope(row.companyMappingId),
    registryVersion: row.registryVersion,
    dimension: row.dimension,
    label: row.label,
    physicalColumn: row.physicalColumn,
    rollups: parseRollups(row.hierarchyJson),
    neverBlend: parseNeverBlend(row.neverBlendAppliesToJson),
    source: row.source,
  };
}

function mapRollupRule(
  row: GovernedMetricRollupGrainRowLike,
): MetricRegistryAggregationRule {
  return {
    dimension: row.dimension,
    rollup: row.rollup,
    ruleFormulaId: row.formulaId,
    materialization: row.materialization as MetricRegistryAggregationRule['materialization'],
  };
}

function mapComponents(
  metric: GovernedMetricRegistryEntryRowLike,
  rows: readonly GovernedMetricComponentRowLike[],
): MetricRegistryComponents | undefined {
  const componentRows = rows.filter((row) => sameRegistryChildScope(row, metric));
  if (componentRows.length === 0) return undefined;

  return {
    numerator: componentRows
      .filter((row) => row.componentRole === 'numerator')
      .map((row) => ({
        metricKey: row.componentMetricKey,
        temporalType: row.temporalType as Extract<MetricTemporalType, 'flow' | 'stock' | 'count'>,
      })),
    denominator: componentRows
      .filter((row) => row.componentRole === 'denominator')
      .map((row) => ({
        metricKey: row.componentMetricKey,
        temporalType: row.temporalType as Extract<MetricTemporalType, 'flow' | 'stock' | 'count'>,
      })),
  };
}

function parseRollups(value: unknown): readonly DimensionCatalogRollup[] {
  const raw = asRecord(value);
  const candidates = Array.isArray(value) ? value : Array.isArray(raw?.rollups) ? raw.rollups : [];
  return candidates.flatMap((candidate): DimensionCatalogRollup[] => {
    const item = asRecord(candidate);
    if (!item || typeof item.rollup !== 'string' || typeof item.label !== 'string') return [];
    return [
      {
        rollup: item.rollup,
        label: item.label,
        parentRollup: typeof item.parentRollup === 'string' ? item.parentRollup : undefined,
      },
    ];
  });
}

function parseNeverBlend(value: unknown): DimensionCatalogEntry['neverBlend'] | undefined {
  if (value === null || value === undefined) return undefined;
  const raw = asRecord(value);
  const appliesToValue = raw?.appliesTo ?? value;
  const appliesTo = parseAppliesTo(appliesToValue);
  return appliesTo.length > 0 ? { appliesTo } : undefined;
}

function parseAppliesTo(value: unknown): readonly MetricRegistryAppliesTo[] {
  if (!Array.isArray(value)) return [{ dimension: 'business_line', members: 'all' }];
  return value.flatMap((candidate): MetricRegistryAppliesTo[] => {
    const item = asRecord(candidate);
    if (!item || typeof item.dimension !== 'string') return [];
    const members = item.members;
    if (members === 'all') return [{ dimension: item.dimension, members: 'all' }];
    if (Array.isArray(members) && members.every((member) => typeof member === 'string')) {
      return [{ dimension: item.dimension, members }];
    }
    return [];
  });
}

function parseBlendPolicy(value: unknown): MetricRegistryBlendPolicy {
  const raw = asRecord(value);
  if (raw?.kind === 'never_blend') return { kind: 'never_blend' };
  if (raw?.kind === 'translate_via' && typeof raw.translationFormulaId === 'string') {
    return { kind: 'translate_via', translationFormulaId: raw.translationFormulaId };
  }
  return { kind: 'additive' };
}

function parseStringArray(value: unknown): readonly string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : [];
}

function parseSignConvention(
  value: unknown,
  direction: string,
): NonNullable<MetricRegistryEntry['signConvention']> {
  const record = asRecord(value);
  const kind = record?.kind;
  return {
    kind:
      kind === 'higher_is_better' || kind === 'lower_is_better' || kind === 'context'
        ? kind
        : (direction as MetricRegistryEntry['direction']),
    costLine: record?.costLine === true,
  };
}

function parseComposition(value: unknown): MetricRegistryEntry['composedFrom'] {
  const record = asRecord(value);
  if (
    !record ||
    typeof record.formulaId !== 'string' ||
    typeof record.formulaVersion !== 'string' ||
    !Array.isArray(record.inputMetricKeys) ||
    !record.inputMetricKeys.every((item) => typeof item === 'string')
  ) {
    return undefined;
  }
  return {
    formulaId: record.formulaId,
    formulaVersion: record.formulaVersion,
    inputMetricKeys: record.inputMetricKeys,
  };
}

function parseTargetMetricRef(
  value: unknown,
): MetricRegistryEntry['targetMetricRef'] {
  const record = asRecord(value);
  if (
    !record ||
    typeof record.metricId !== 'string' ||
    typeof record.registryVersion !== 'string'
  ) {
    return undefined;
  }
  return {
    metricId: record.metricId,
    registryVersion: record.registryVersion,
  };
}

function parseGovernedMetricDefinitionReferences(value: unknown): Readonly<{
  targetMetricRef?: MetricRegistryTargetRef;
  targetComparisonAnchor?: MetricTargetComparisonAnchor;
}> {
  const directTargetMetricRef = parseTargetMetricRef(value);
  if (directTargetMetricRef) return { targetMetricRef: directTargetMetricRef };
  const record = asRecord(value);
  return {
    targetMetricRef: parseTargetMetricRef(record?.targetMetricRef),
    targetComparisonAnchor: parseTargetComparisonAnchor(
      record?.targetComparisonAnchor,
    ),
  };
}

function parseTargetComparisonAnchor(
  value: unknown,
): MetricTargetComparisonAnchor | undefined {
  const record = asRecord(value);
  const metricRef = parseTargetMetricRef(record?.metricRef);
  if (record?.relation !== 'target_gap_of' || !metricRef) return undefined;
  return { relation: 'target_gap_of', metricRef };
}

function sameTargetMetricRef(
  left: MetricRegistryTargetRef | undefined,
  right: MetricRegistryTargetRef | undefined,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.metricId === right.metricId &&
    left.registryVersion === right.registryVersion
  );
}

function sameTargetComparisonAnchor(
  left: MetricTargetComparisonAnchor | undefined,
  right: MetricTargetComparisonAnchor | undefined,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.relation === right.relation &&
    sameTargetMetricRef(left.metricRef, right.metricRef)
  );
}

function sameRegistryChildScope(
  child: Readonly<{
    tenantId?: string;
    registryLayer?: string;
    industryProfileId?: string;
    companyMappingId?: string;
    metricKey: string;
    registryVersion: string;
  }>,
  metric: GovernedMetricRegistryEntryRowLike,
): boolean {
  return (
    (child.tenantId ?? METRIC_REGISTRY_GLOBAL_TENANT_ID) ===
      (metric.tenantId ?? METRIC_REGISTRY_GLOBAL_TENANT_ID) &&
    (child.registryLayer ?? 'core') === metric.registryLayer &&
    (child.industryProfileId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY) ===
      (metric.industryProfileId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY) &&
    (child.companyMappingId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY) ===
      (metric.companyMappingId ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY) &&
    child.metricKey === metric.metricKey &&
    child.registryVersion === metric.registryVersion
  );
}

function normalizeGlobalScope(value: string | undefined): string | undefined {
  return value && value !== GOVERNED_METRIC_FACT_ALL_MEMBER_KEY ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}
